import { PhoneClickButtonType, PhoneClickItemPlacement } from '@helpers/phoneClickTracking';
import { getModifiers } from '@helpers/functions';
import { ButtonModel } from '@_types/types';
import Icon from '@atoms/icons/Icon';

export interface PhoneRevealButtonModel extends Omit<ButtonModel, 'href' | 'link' | 'text'> {
  /** Visible label before reveal (e.g. Office, Mobile). */
  label: string;
  /** Display phone number shown after reveal. */
  phoneNumber: string;
  /** tel: href used after reveal / on dial. */
  phoneHref: string;
  buttonType?: PhoneClickButtonType;
  itemPlacement?: PhoneClickItemPlacement;
  /** Optional id for GTM targeting (e.g. phone-reveal-key-contact). */
  id?: string;
  /** Show phone icon before reveal (removed on reveal to match Figma). */
  showIcon?: boolean;
}

/**
 * Click-to-call reveal control.
 * All viewports: first click/tap reveals + phone_click; second click/tap dials via tel:.
 */
export default function PhoneRevealButton(model: PhoneRevealButtonModel) {
  const {
    label,
    phoneNumber,
    phoneHref,
    buttonType = 'tertiary',
    itemPlacement,
    id,
    icon,
    iconAfter,
    className,
    styleModifier = ['tertiary', 'large'],
    dataSet,
    showIcon = true,
  } = model;

  const styleModifiers = getModifiers({ ...model, styleModifier }, 'kf-a-button') + (className ? ` ${className}` : '');

  const phoneClickPayload =
    itemPlacement != null
      ? JSON.stringify({
          button_type: buttonType,
          item_placement: itemPlacement,
        })
      : undefined;

  const phoneIcon =
    icon ??
    (showIcon
      ? {
          iconPath: '#kf-phone',
          viewBoxHeight: 20,
          viewBoxWidth: 20,
        }
      : undefined);

  return (
    <a
      id={id}
      className={styleModifiers}
      href="#"
      aria-label={label}
      {...dataSet}
      data-phone-reveal=""
      data-phone-number={phoneNumber}
      data-phone-href={phoneHref}
      {...(phoneClickPayload ? { 'data-phone-click': phoneClickPayload } : {})}
    >
      {phoneIcon && <Icon {...phoneIcon} className="kf-a-button__icon-before" />}
      {label && <span>{label}</span>}
      {iconAfter && <Icon {...iconAfter} className="kf-a-button__icon-after" />}
    </a>
  );
}
